import { useContext } from 'react';
import styled from 'styled-components';
import { GroupedCashFlow, CashFlowCollection, GroupedCashFlows, CashFlowCalendarEntry } from '@algoan/dashboard/model';
import { useDispatch, useSelector } from '@algoan/dashboard/store/store';
import { AmplitudeEvent, logAmplitudeEvent } from '@algoan/dashboard/analytics';
import { useTransactionCategoryTranslator, useTransactionTypeTranslator } from '../../messages';
import { sumTotalForCashFlow, sumTotalForCategory } from '@algoan/dashboard/calculators';
import { isCashFlowCollection, useDashboardParams, Flag, useFeatureFlag } from '@algoan/dashboard/utils';
import {
  isRecategorizationEnabledSelector,
  checkedCashFlowsSelector,
  customerSelector,
  latestAnalysisSelector,
} from '@algoan/dashboard/store/selectors';
import { toggleCashFlow, toggleCashFlowCollection } from '@algoan/dashboard/store/slice';
import { saveSelectedCashFlows } from '@algoan/dashboard/store/thunks';
import { OpenRecategorizationModalContext } from '../cash-flow-modal/use-recategorization-modal';
import { CheckBox, booleanToCheckedState, CheckedState, FormattedMoney } from '../atomic-components';
import { MonthlyAmountCashFlow } from './monthly-amount-cash-flow';

interface CashFlowRowProps extends React.ComponentPropsWithoutRef<'tr'> {
  calendar: GroupedCashFlows['calendar'];
  cashFlow: GroupedCashFlow | CashFlowCollection;
  checkable: boolean;
  headerExists?: boolean;
  isEven: boolean;
  firstColumn?: React.ReactNode;
  dataTestId?: string;
}

const isCashFlowCollectionChecked = (
  cashFlowCollection: CashFlowCollection,
  checkedRows: { [key: number]: boolean },
): CheckedState => {
  if (cashFlowCollection.cashFlows.every((cashFlow) => checkedRows[cashFlow.index])) {
    return CheckedState.TRUE;
  } else if (cashFlowCollection.cashFlows.some((cashFlow) => checkedRows[cashFlow.index])) {
    return CheckedState.INTERMEDIATE;
  }

  return CheckedState.FALSE;
};

export const CashFlowRow: React.FC<CashFlowRowProps> = ({
  calendar,
  cashFlow,
  checkable,
  className,
  headerExists,
  isEven,
  firstColumn,
  dataTestId,
}) => {
  const dispatch = useDispatch();
  const checkedRows = useSelector(checkedCashFlowsSelector);
  const translateTransactionCategory = useTransactionCategoryTranslator();
  const translateTransactionType = useTransactionTypeTranslator();

  const { customerId } = useDashboardParams();
  const customer = useSelector(customerSelector(customerId));
  const analysis = useSelector(latestAnalysisSelector);
  const analysisId = analysis?.id;
  const { openModalWithCashFlow } = useContext(OpenRecategorizationModalContext);
  const isRecategorizationEnabled = useSelector(isRecategorizationEnabledSelector);
  const { active: areTransactionHidden } = useFeatureFlag(Flag.HIDE_THE_TRANSACTIONS);

  const saveCheckState = (cashFlow: GroupedCashFlow | CashFlowCollection, isChecked: boolean): void => {
    const cashFlowsIds: string[] = [];

    if (!isCashFlowCollection(cashFlow)) {
      if (cashFlow.id) {
        cashFlowsIds.push(cashFlow.id);
      }
    } else {
      cashFlowsIds.push(
        ...cashFlow.cashFlows
          .map((subCashFlow) => subCashFlow.id)
          .filter<string>((cashFlowId): cashFlowId is string => !!cashFlowId),
      );
    }

    if (analysisId && cashFlowsIds.length > 0) {
      dispatch(saveSelectedCashFlows(customerId, analysisId, cashFlowsIds, isChecked));
    }
  };

  const selectRow = (cashFlow: GroupedCashFlow | CashFlowCollection): void => {
    if (isCashFlowCollection(cashFlow)) {
      dispatch(toggleCashFlowCollection(cashFlow.cashFlows));
    } else {
      dispatch(toggleCashFlow(cashFlow.index));
    }

    const isChecked = !(isCashFlowCollection(cashFlow)
      ? cashFlow.cashFlows.every((cashFlow) => checkedRows[cashFlow.index])
      : checkedRows[cashFlow.index]);

    saveCheckState(cashFlow, isChecked);

    if (isChecked) {
      logAmplitudeEvent(AmplitudeEvent.ANALYSE_UNCHECK_REMAIN_TO_LIVE, { categoryName: cashFlow.category });
    } else {
      logAmplitudeEvent(AmplitudeEvent.ANALYSE_CHECK_REMAIN_TO_LIVE, { categoryName: cashFlow.category });
    }
  };

  const renderLabel = (): React.ReactElement => {
    if (isCashFlowCollection(cashFlow)) {
      return <strong>{translateTransactionCategory(cashFlow.category)}</strong>;
    } else {
      switch (cashFlow.labelStrategy ?? 'default') {
        case 'labelRoot':
          return <>{cashFlow.labelRoot?.toLowerCase()}</>;
        case 'categoryAndType':
          return (
            <>
              <strong>{translateTransactionCategory(cashFlow.category)}</strong>
              {` - ${translateTransactionType(cashFlow.type)}`}
            </>
          );
        case 'onlyCategory':
          return <strong>{translateTransactionCategory(cashFlow.category)}</strong>;
        case 'default':
          return (
            <>
              <strong>{translateTransactionCategory(cashFlow.category)}</strong>
              {cashFlow.label && cashFlow.label !== 'unknown' ? ` - ${cashFlow.label.toLowerCase()}` : ''}
            </>
          );
      }
    }
  };

  return (
    <S.BodyRow
      even={isEven}
      headerExists={!!headerExists}
      className={className}
      isRecategorizationEnabled={isRecategorizationEnabled}
      data-testid={dataTestId}
    >
      <td
        data-testid={`cash-flow-row-${cashFlow.category}`}
        className={!isCashFlowCollection(cashFlow) && !areTransactionHidden ? 'cashflow' : ''}
        onClick={() => {
          if (isCashFlowCollection(cashFlow) || areTransactionHidden) {
            return;
          }
          openModalWithCashFlow(cashFlow);
          logAmplitudeEvent(AmplitudeEvent.CASHFLOW_OPEN, {
            category: cashFlow.category,
            tabPage: 'Analyse',
            fileNumber: customer?.customIdentifier,
          });
        }}
      >
        <div>{firstColumn || renderLabel()}</div>
      </td>
      {checkable ? (
        <S.ClickableCell
          onClick={() => {
            selectRow(cashFlow);
          }}
        >
          <CheckBox
            dataTestId="cash-flow-row-checkbox"
            checked={
              isCashFlowCollection(cashFlow)
                ? isCashFlowCollectionChecked(cashFlow, checkedRows)
                : booleanToCheckedState(checkedRows[cashFlow.index])
            }
            variant="deprecated"
          />
        </S.ClickableCell>
      ) : (
        <td />
      )}
      {calendar.map((calendar) => (
        <td className="amount-cell" key={`cash-flow-${calendar.month}`}>
          <div>
            {isCashFlowCollection(cashFlow) &&
              cashFlow.cashFlows.some((subCashFlow) => calendar.month in subCashFlow.calendar) && (
                <MonthlyAmountCashFlow
                  calendarEntry={cashFlow.cashFlows.reduce(
                    (total, cashFlow) => {
                      const totalRejectedAmount = cashFlow.calendar[calendar.month]?.totalRejectedAmount ?? 0;
                      const amount = cashFlow.calendar[calendar.month]?.amount ?? 0;

                      return {
                        totalRejectedAmount: totalRejectedAmount + total.totalRejectedAmount,
                        amount: amount + total.amount,
                      };
                    },
                    { totalRejectedAmount: 0, amount: 0 },
                  )}
                />
              )}

            {!isCashFlowCollection(cashFlow) && calendar.month in cashFlow.calendar && (
              <MonthlyAmountCashFlow calendarEntry={cashFlow.calendar[calendar.month] as CashFlowCalendarEntry} />
            )}
          </div>
        </td>
      ))}
      <td>
        <div>
          {isCashFlowCollection(cashFlow) && (
            <FormattedMoney number={Math.abs(sumTotalForCategory(cashFlow.cashFlows, checkedRows))} />
          )}
          {!isCashFlowCollection(cashFlow) && (
            <FormattedMoney number={Math.abs(checkedRows[cashFlow.index] ? sumTotalForCashFlow(cashFlow) : 0)} />
          )}
        </div>
      </td>
    </S.BodyRow>
  );
};

const S = {
  BodyRow: styled.tr<{ even: boolean; headerExists: boolean; isRecategorizationEnabled: boolean }>`
    font-weight: 500;
    font-size: 11px;
    line-height: 14px;

    ${(props) =>
      props.isRecategorizationEnabled
        ? `
    .cashflow:hover {
      z-index: 1;
      position: relative;
      box-shadow: 0px 0px 5px 0px #00466b33;
      cursor: pointer;
    }`
        : ``}

    td {
      background-color: ${(props) => (props.even ? 'transparent' : '#E0E8EC')};
      border-right: white 2px solid;
      padding: 1px 0;
      text-align: right;
      max-width: 60px;
      justify-content: space-between;

      &:first-child {
        border-radius: 4px 0 0 4px;
        padding-left: 22px;
        text-align: left;
        border-right: white 2px solid;
        min-width: 200px;

        strong {
          font-weight: bold;
        }
      }

      &:last-child {
        border-radius: 0 4px 4px 0;
        border-right: none;
      }
    }
    .amount-cell {
      padding-right: 6px;
    }
  `,
  ClickableCell: styled.td`
    cursor: pointer;
  `,
};
